import {
    getAdminMaterialRequests,
    updateAdminMaterialRequestStatus,
    downloadAdminMaterialRequestFile,
    getAdminPublishers,
    deleteAdminPublisher,
    migrateAdminPublisher,
    getAdminGuestLinks,
    updateProviderVisibility,
    getProviderTeam,
    assignProviderToUser,
    removeProviderFromUser,
} from '@/api/publisherAdmin.js';
import constants from '@/constants';

// ---------------------------------------------------------------------------
// Dev-only logging helper
// ---------------------------------------------------------------------------
const devLog = (actionName, message, data = null) => {
    if (process.env.NODE_ENV !== 'production') {
        if (data !== null) {
            console.info(`[publisherAdmin.${actionName}] ${message}`, data);
        } else {
            console.info(`[publisherAdmin.${actionName}] ${message}`);
        }
    }
};

export default {
    state: {
        // ── Material Requests ───────────────────────────────────────────────
        materialRequests: [],
        materialRequestsTotal: 0,
        materialRequestsPage: 1,
        materialRequestsPerPage: 20,
        materialRequestsLoading: false,
        materialRequestsBadRequest: false,
        materialRequestsFilters: {
            status: null,
            publisher_id: null,
        },
        selectedRequest: null,

        // ── Publishers ──────────────────────────────────────────────────────
        publishers: [],
        publishersTotal: 0,
        publishersPage: 1,
        publishersPerPage: 20,
        publishersLoading: false,
        publishersBadRequest: false,
        publishersFilters: {
            search: null,
            status: null,
        },
        selectedPublisher: null,

        // ── Guest Links ─────────────────────────────────────────────────────
        guestLinks: [],
        guestLinksTotal: 0,
        guestLinksPage: 1,
        guestLinksPerPage: 20,
        guestLinksLoading: false,
        guestLinksBadRequest: false,
        // ── Provider Teams (keyed by provider_id) ─────────────────────────
        // Shape: { [provider_id]: { items: [], loading: false, error: false } }
        providerTeams: {},    },

    getters: {
        // Material Requests
        ADMIN_MATERIAL_REQUESTS: (state) => state.materialRequests,
        ADMIN_MATERIAL_REQUESTS_TOTAL: (state) => state.materialRequestsTotal,
        ADMIN_MATERIAL_REQUESTS_PAGE: (state) => state.materialRequestsPage,
        ADMIN_MATERIAL_REQUESTS_PER_PAGE: (state) =>
            state.materialRequestsPerPage,
        ADMIN_MATERIAL_REQUESTS_LOADING: (state) =>
            state.materialRequestsLoading,
        ADMIN_MATERIAL_REQUESTS_BAD_REQUEST: (state) =>
            state.materialRequestsBadRequest,
        ADMIN_MATERIAL_REQUESTS_FILTERS: (state) =>
            state.materialRequestsFilters,
        ADMIN_SELECTED_REQUEST: (state) => state.selectedRequest,

        // Publishers
        ADMIN_PUBLISHERS: (state) => state.publishers,
        ADMIN_PUBLISHERS_TOTAL: (state) => state.publishersTotal,
        ADMIN_PUBLISHERS_PAGE: (state) => state.publishersPage,
        ADMIN_PUBLISHERS_PER_PAGE: (state) => state.publishersPerPage,
        ADMIN_PUBLISHERS_LOADING: (state) => state.publishersLoading,
        ADMIN_PUBLISHERS_BAD_REQUEST: (state) => state.publishersBadRequest,
        ADMIN_PUBLISHERS_FILTERS: (state) => state.publishersFilters,
        ADMIN_SELECTED_PUBLISHER: (state) => state.selectedPublisher,

        // Guest Links
        ADMIN_GUEST_LINKS: (state) => state.guestLinks,
        ADMIN_GUEST_LINKS_TOTAL: (state) => state.guestLinksTotal,
        ADMIN_GUEST_LINKS_PAGE: (state) => state.guestLinksPage,
        ADMIN_GUEST_LINKS_PER_PAGE: (state) => state.guestLinksPerPage,
        ADMIN_GUEST_LINKS_LOADING: (state) => state.guestLinksLoading,
        ADMIN_GUEST_LINKS_BAD_REQUEST: (state) => state.guestLinksBadRequest,

        // Computed: total pages for Material Requests
        ADMIN_MATERIAL_REQUESTS_TOTAL_PAGES: (state) =>
            Math.ceil(state.materialRequestsTotal / state.materialRequestsPerPage) ||
            1,

        // Computed: total pages for Publishers
        ADMIN_PUBLISHERS_TOTAL_PAGES: (state) =>
            Math.ceil(state.publishersTotal / state.publishersPerPage) || 1,

        // Computed: total pages for Guest Links
        ADMIN_GUEST_LINKS_TOTAL_PAGES: (state) =>
            Math.ceil(state.guestLinksTotal / state.guestLinksPerPage) || 1,

        // Provider Teams
        PROVIDER_TEAM: (state) => (id) =>
            state.providerTeams[id] || { items: [], loading: false, error: false },
    },

    mutations: {
        // ── Material Requests setters ───────────────────────────────────────
        SET_ADMIN_MATERIAL_REQUESTS(state, payload) {
            state.materialRequests = payload;
        },
        SET_ADMIN_MATERIAL_REQUESTS_TOTAL(state, payload) {
            state.materialRequestsTotal = payload;
        },
        SET_ADMIN_MATERIAL_REQUESTS_PAGE(state, payload) {
            state.materialRequestsPage = payload;
        },
        SET_ADMIN_MATERIAL_REQUESTS_PER_PAGE(state, payload) {
            state.materialRequestsPerPage = payload;
        },
        SET_ADMIN_MATERIAL_REQUESTS_LOADING(state, payload) {
            state.materialRequestsLoading = payload;
        },
        SET_ADMIN_MATERIAL_REQUESTS_BAD_REQUEST(state, payload) {
            state.materialRequestsBadRequest = payload;
        },
        SET_ADMIN_MATERIAL_REQUESTS_FILTERS(state, payload) {
            state.materialRequestsFilters = {
                ...state.materialRequestsFilters,
                ...payload,
            };
        },
        SET_ADMIN_SELECTED_REQUEST(state, payload) {
            state.selectedRequest = payload;
        },

        // Optimistic update of a single material request in the list
        UPDATE_ADMIN_MATERIAL_REQUEST_IN_LIST(state, updatedRequest) {
            const index = state.materialRequests.findIndex(
                (r) => r.id === updatedRequest.id
            );
            if (index !== -1) {
                state.materialRequests.splice(index, 1, updatedRequest);
            }
        },

        // ── Publishers setters ──────────────────────────────────────────────
        SET_ADMIN_PUBLISHERS(state, payload) {
            state.publishers = payload;
        },
        SET_ADMIN_PUBLISHERS_TOTAL(state, payload) {
            state.publishersTotal = payload;
        },
        SET_ADMIN_PUBLISHERS_PAGE(state, payload) {
            state.publishersPage = payload;
        },
        SET_ADMIN_PUBLISHERS_PER_PAGE(state, payload) {
            state.publishersPerPage = payload;
        },
        SET_ADMIN_PUBLISHERS_LOADING(state, payload) {
            state.publishersLoading = payload;
        },
        SET_ADMIN_PUBLISHERS_BAD_REQUEST(state, payload) {
            state.publishersBadRequest = payload;
        },
        SET_ADMIN_PUBLISHERS_FILTERS(state, payload) {
            state.publishersFilters = {
                ...state.publishersFilters,
                ...payload,
            };
        },
        SET_ADMIN_SELECTED_PUBLISHER(state, payload) {
            state.selectedPublisher = payload;
        },

        // ── Guest Links setters ─────────────────────────────────────────────
        SET_ADMIN_GUEST_LINKS(state, payload) {
            state.guestLinks = payload;
        },
        SET_ADMIN_GUEST_LINKS_TOTAL(state, payload) {
            state.guestLinksTotal = payload;
        },
        SET_ADMIN_GUEST_LINKS_PAGE(state, payload) {
            state.guestLinksPage = payload;
        },
        SET_ADMIN_GUEST_LINKS_PER_PAGE(state, payload) {
            state.guestLinksPerPage = payload;
        },
        SET_ADMIN_GUEST_LINKS_LOADING(state, payload) {
            state.guestLinksLoading = payload;
        },
        SET_ADMIN_GUEST_LINKS_BAD_REQUEST(state, payload) {
            state.guestLinksBadRequest = payload;
        },

        // ── Publisher in-list updater ──────────────────────────────────
        UPDATE_ADMIN_PUBLISHER_IN_LIST(state, updated) {
            const idx = state.publishers.findIndex((p) => p.id === updated.id);
            if (idx !== -1) state.publishers.splice(idx, 1, updated);
        },

        // ── Provider Teams mutations ──────────────────────────────────
        SET_PROVIDER_TEAM_LOADING(state, { providerId, loading }) {
            const existing = state.providerTeams[providerId] || { items: [], error: false };
            state.providerTeams = {
                ...state.providerTeams,
                [providerId]: { ...existing, loading },
            };
        },
        SET_PROVIDER_TEAM_ITEMS(state, { providerId, items }) {
            const existing = state.providerTeams[providerId] || { loading: false, error: false };
            state.providerTeams = {
                ...state.providerTeams,
                [providerId]: { ...existing, items },
            };
        },
        SET_PROVIDER_TEAM_ERROR(state, { providerId, error }) {
            const existing = state.providerTeams[providerId] || { items: [], loading: false };
            state.providerTeams = {
                ...state.providerTeams,
                [providerId]: { ...existing, error },
            };
        },
        REMOVE_TEAM_MEMBER_FROM_LIST(state, { providerId, userId }) {
            const entry = state.providerTeams[providerId];
            if (!entry) return;
            state.providerTeams = {
                ...state.providerTeams,
                [providerId]: {
                    ...entry,
                    items: entry.items.filter((m) => m.user_id !== userId),
                },
            };
        },
        UPDATE_TEAM_MEMBER_ROLE_IN_LIST(state, { providerId, userId, role }) {
            const entry = state.providerTeams[providerId];
            if (!entry) return;
            state.providerTeams = {
                ...state.providerTeams,
                [providerId]: {
                    ...entry,
                    items: entry.items.map((m) =>
                        m.user_id === userId ? { ...m, role } : m
                    ),
                },
            };
        },
    },

    actions: {
        // ── Material Requests ───────────────────────────────────────────────

        /**
         * Load paginated material requests using current state filters and page.
         */
        async loadAdminMaterialRequests({ commit, state }) {
            devLog('loadAdminMaterialRequests', 'Loading...', {
                page: state.materialRequestsPage,
                per_page: state.materialRequestsPerPage,
                filters: state.materialRequestsFilters,
            });

            commit('SET_ADMIN_MATERIAL_REQUESTS_LOADING', true);
            commit('SET_ADMIN_MATERIAL_REQUESTS_BAD_REQUEST', false);

            try {
                const params = {
                    page: state.materialRequestsPage,
                    per_page: state.materialRequestsPerPage,
                };

                if (state.materialRequestsFilters.status) {
                    params.status = state.materialRequestsFilters.status;
                }
                if (state.materialRequestsFilters.publisher_id) {
                    params.publisher_id =
                        state.materialRequestsFilters.publisher_id;
                }

                const response = await getAdminMaterialRequests(params);

                devLog('loadAdminMaterialRequests', 'Success', {
                    total: response.total,
                    count: response.items?.length,
                });

                commit(
                    'SET_ADMIN_MATERIAL_REQUESTS',
                    response.items || []
                );
                commit(
                    'SET_ADMIN_MATERIAL_REQUESTS_TOTAL',
                    response.total || 0
                );
                commit(
                    'SET_ADMIN_MATERIAL_REQUESTS_PAGE',
                    response.page || 1
                );
                commit(
                    'SET_ADMIN_MATERIAL_REQUESTS_PER_PAGE',
                    response.per_page || 20
                );
            } catch (error) {
                devLog('loadAdminMaterialRequests', 'Error', {
                    status: error?.response?.status,
                    message: error?.message,
                });
                commit('SET_ADMIN_MATERIAL_REQUESTS_BAD_REQUEST', true);
                commit('SET_ADMIN_MATERIAL_REQUESTS', []);
                commit('SET_ADMIN_MATERIAL_REQUESTS_TOTAL', 0);
            } finally {
                commit('SET_ADMIN_MATERIAL_REQUESTS_LOADING', false);
            }
        },

        /**
         * Update the status of a single material request.
         * Performs an optimistic update in the list, then confirms via API.
         * Shows a success/error popup notification.
         *
         * @param {object} param
         * @param {string|number} param.requestId
         * @param {string} param.status - new status value
         * @param {string} [param.adminNotes] - admin notes (NOT logged)
         */
        async updateAdminMaterialRequestStatus(
            { commit },
            { requestId, status, adminNotes }
        ) {
            devLog('updateAdminMaterialRequestStatus', 'Updating status', {
                requestId,
                status,
                // adminNotes intentionally not logged
            });

            try {
                const updatedRequest = await updateAdminMaterialRequestStatus(
                    requestId,
                    { status, admin_notes: adminNotes }
                );

                devLog(
                    'updateAdminMaterialRequestStatus',
                    'Success',
                    { requestId, newStatus: updatedRequest?.status }
                );

                commit(
                    'UPDATE_ADMIN_MATERIAL_REQUEST_IN_LIST',
                    updatedRequest
                );

                commit('POPUP_ADD', {
                    title: 'Status Updated',
                    description: `Request status changed to "${status}".`,
                    color: constants.COLOR.GREEN,
                });

                return updatedRequest;
            } catch (error) {
                devLog(
                    'updateAdminMaterialRequestStatus',
                    'Error',
                    {
                        requestId,
                        status: error?.response?.status,
                        message: error?.message,
                    }
                );

                commit('POPUP_ADD', {
                    title: 'Update Failed',
                    description:
                        error?.response?.data?.detail ||
                        'Failed to update request status.',
                    color: constants.COLOR.RED,
                });

                throw error;
            }
        },

        /**
         * Get a presigned download URL for a file attached to a material request
         * and open it in a new tab.
         * @param {Object} param
         * @param {number|string} param.requestId
         * @param {number|string} param.fileId
         */
        async downloadAdminMaterialRequestFile(
            { commit },
            { requestId, fileId }
        ) {
            devLog('downloadAdminMaterialRequestFile', 'Downloading file', {
                requestId,
                fileId,
            });

            try {
                const result = await downloadAdminMaterialRequestFile(
                    requestId,
                    fileId
                );
                if (result?.download_url) {
                    window.open(result.download_url, '_blank', 'noopener,noreferrer');
                }
                return result;
            } catch (error) {
                devLog(
                    'downloadAdminMaterialRequestFile',
                    'Error',
                    {
                        requestId,
                        fileId,
                        status: error?.response?.status,
                        message: error?.message,
                    }
                );

                commit('POPUP_ADD', {
                    title: 'Download Failed',
                    description:
                        error?.response?.data?.detail ||
                        'Failed to generate download link.',
                    color: constants.COLOR.RED,
                });

                throw error;
            }
        },

        // ── Publishers ──────────────────────────────────────────────────────

        /**
         * Load paginated publishers using current state filters and page.
         */
        async loadAdminPublishers({ commit, state }) {
            devLog('loadAdminPublishers', 'Loading...', {
                page: state.publishersPage,
                per_page: state.publishersPerPage,
                filters: state.publishersFilters,
            });

            commit('SET_ADMIN_PUBLISHERS_LOADING', true);
            commit('SET_ADMIN_PUBLISHERS_BAD_REQUEST', false);

            try {
                const params = {
                    page: state.publishersPage,
                    per_page: state.publishersPerPage,
                };

                const trimmedSearch = state.publishersFilters.search?.trim();
                if (trimmedSearch) {
                    params.search = trimmedSearch;
                }
                if (state.publishersFilters.status) {
                    params.status = state.publishersFilters.status;
                }

                const response = await getAdminPublishers(params);

                devLog('loadAdminPublishers', 'Success', {
                    total: response.total,
                    count: response.items?.length,
                });

                commit('SET_ADMIN_PUBLISHERS', response.items || []);
                commit('SET_ADMIN_PUBLISHERS_TOTAL', response.total || 0);
                commit('SET_ADMIN_PUBLISHERS_PAGE', response.page || 1);
                commit(
                    'SET_ADMIN_PUBLISHERS_PER_PAGE',
                    response.per_page || 20
                );
            } catch (error) {
                devLog('loadAdminPublishers', 'Error', {
                    status: error?.response?.status,
                    message: error?.message,
                });
                commit('SET_ADMIN_PUBLISHERS_BAD_REQUEST', true);
                commit('SET_ADMIN_PUBLISHERS', []);
                commit('SET_ADMIN_PUBLISHERS_TOTAL', 0);
            } finally {
                commit('SET_ADMIN_PUBLISHERS_LOADING', false);
            }
        },

        /**
         * Delete a publisher by ID, then reload the publisher list.
         * Shows a success/error popup notification.
         *
         * @param {string|number} publisherId
         */
        async deleteAdminPublisher({ commit, dispatch }, publisherId) {
            devLog('deleteAdminPublisher', 'Deleting publisher', {
                publisherId,
            });

            try {
                await deleteAdminPublisher(publisherId);

                devLog('deleteAdminPublisher', 'Success', { publisherId });

                commit('POPUP_ADD', {
                    title: 'Publisher Deleted',
                    description: 'The publisher has been successfully deleted.',
                    color: constants.COLOR.GREEN,
                });

                // Reset to page 1 and reload
                commit('SET_ADMIN_PUBLISHERS_PAGE', 1);
                await dispatch('loadAdminPublishers');
            } catch (error) {
                devLog('deleteAdminPublisher', 'Error', {
                    publisherId,
                    status: error?.response?.status,
                    message: error?.message,
                });

                commit('POPUP_ADD', {
                    title: 'Delete Failed',
                    description:
                        error?.response?.data?.detail ||
                        'Failed to delete publisher.',
                    color: constants.COLOR.RED,
                });

                throw error;
            }
        },

        /**
         * Migrate a publisher to a different provider target.
         * Shows a success/error popup notification.
         *
         * @param {object} param
         * @param {string|number} param.publisherId
         * @param {string|number} param.targetProviderId
         */
        async migrateAdminPublisher(
            { commit, dispatch },
            { publisherId, targetProviderId }
        ) {
            devLog('migrateAdminPublisher', 'Migrating publisher', {
                publisherId,
                targetProviderId,
            });

            try {
                await migrateAdminPublisher(publisherId, {
                    target_provider_id: targetProviderId,
                });

                devLog('migrateAdminPublisher', 'Success', {
                    publisherId,
                    targetProviderId,
                });

                commit('POPUP_ADD', {
                    title: 'Publisher Migrated',
                    description: 'The publisher has been successfully migrated.',
                    color: constants.COLOR.GREEN,
                });

                // Reset to page 1 and reload after migration
                commit('SET_ADMIN_PUBLISHERS_PAGE', 1);
                await dispatch('loadAdminPublishers');
            } catch (error) {
                devLog('migrateAdminPublisher', 'Error', {
                    publisherId,
                    targetProviderId,
                    status: error?.response?.status,
                    message: error?.message,
                });

                commit('POPUP_ADD', {
                    title: 'Migration Failed',
                    description:
                        error?.response?.data?.detail ||
                        'Failed to migrate publisher.',
                    color: constants.COLOR.RED,
                });

                throw error;
            }
        },

        // ── Provider Visibility ─────────────────────────────────────────────

        /**
         * Toggle provider portal visibility (is_public).
         * Updates the publisher in-list optimistically after API success.
         * @param {{ id: number, isPublic: boolean }} payload
         */
        async updateProviderVisibility({ commit }, { id, isPublic }) {
            devLog('updateProviderVisibility', 'Updating visibility', {
                id,
                isPublic,
            });
            try {
                const updated = await updateProviderVisibility(id, isPublic);
                if (updated) {
                    commit('UPDATE_ADMIN_PUBLISHER_IN_LIST', updated);
                }
                commit('POPUP_ADD', {
                    title: isPublic ? 'Provider Made Public' : 'Provider Hidden',
                    description: isPublic
                        ? 'Provider is now visible in the portal.'
                        : 'Provider is now hidden from the portal.',
                    color: constants.COLOR.GREEN,
                });
            } catch (error) {
                devLog('updateProviderVisibility', 'Error', {
                    id,
                    status: error?.response?.status,
                    message: error?.message,
                });
                commit('POPUP_ADD', {
                    title: 'Visibility Update Failed',
                    description:
                        error?.response?.data?.detail ||
                        'Failed to update provider visibility.',
                    color: constants.COLOR.RED,
                });
                throw error;
            }
        },

        // ── Provider Teams ──────────────────────────────────────────────────

        /**
         * Load team members for a provider.
         * @param {number} providerId
         */
        async loadProviderTeam({ commit }, providerId) {
            devLog('loadProviderTeam', 'Loading team', { providerId });
            commit('SET_PROVIDER_TEAM_LOADING', { providerId, loading: true });
            commit('SET_PROVIDER_TEAM_ERROR', { providerId, error: false });
            try {
                const items = await getProviderTeam(providerId);
                devLog('loadProviderTeam', 'Success', {
                    providerId,
                    count: items?.length,
                });
                commit('SET_PROVIDER_TEAM_ITEMS', { providerId, items: items || [] });
            } catch (error) {
                devLog('loadProviderTeam', 'Error', {
                    providerId,
                    status: error?.response?.status,
                    message: error?.message,
                });
                commit('SET_PROVIDER_TEAM_ERROR', { providerId, error: true });
                commit('SET_PROVIDER_TEAM_ITEMS', { providerId, items: [] });
            } finally {
                commit('SET_PROVIDER_TEAM_LOADING', { providerId, loading: false });
            }
        },

        /**
         * Update a team member's role. Re-uses the idempotent assign endpoint.
         * @param {{ providerId: number, userId: number, role: string }} payload
         */
        async updateTeamMemberRole({ commit }, { providerId, userId, role }) {
            devLog('updateTeamMemberRole', 'Updating role', {
                providerId,
                userId,
                role,
            });
            try {
                await assignProviderToUser(userId, {
                    provider_id: providerId,
                    role,
                });
                commit('UPDATE_TEAM_MEMBER_ROLE_IN_LIST', { providerId, userId, role });
            } catch (error) {
                devLog('updateTeamMemberRole', 'Error', {
                    providerId,
                    userId,
                    status: error?.response?.status,
                    message: error?.message,
                });
                commit('POPUP_ADD', {
                    title: 'Role Update Failed',
                    description:
                        error?.response?.data?.detail ||
                        'Failed to update team member role.',
                    color: constants.COLOR.RED,
                });
                throw error;
            }
        },

        /**
         * Remove (deactivate) a team member from a provider.
         * @param {{ providerId: number, userId: number }} payload
         */
        async removeTeamMember({ commit }, { providerId, userId }) {
            devLog('removeTeamMember', 'Removing member', { providerId, userId });
            try {
                await removeProviderFromUser(userId, providerId);
                commit('REMOVE_TEAM_MEMBER_FROM_LIST', { providerId, userId });
                commit('POPUP_ADD', {
                    title: 'Team Member Removed',
                    description: 'The team member has been removed from this provider.',
                    color: constants.COLOR.GREEN,
                });
            } catch (error) {
                devLog('removeTeamMember', 'Error', {
                    providerId,
                    userId,
                    status: error?.response?.status,
                    message: error?.message,
                });
                commit('POPUP_ADD', {
                    title: 'Remove Failed',
                    description:
                        error?.response?.data?.detail ||
                        'Failed to remove team member.',
                    color: constants.COLOR.RED,
                });
                throw error;
            }
        },

        // ── Guest Links ─────────────────────────────────────────────────────

        /**
         * Load paginated guest links using current state page.
         */
        async loadAdminGuestLinks({ commit, state }) {
            devLog('loadAdminGuestLinks', 'Loading...', {
                page: state.guestLinksPage,
                per_page: state.guestLinksPerPage,
            });

            commit('SET_ADMIN_GUEST_LINKS_LOADING', true);
            commit('SET_ADMIN_GUEST_LINKS_BAD_REQUEST', false);

            try {
                const params = {
                    page: state.guestLinksPage,
                    per_page: state.guestLinksPerPage,
                };

                const response = await getAdminGuestLinks(params);

                devLog('loadAdminGuestLinks', 'Success', {
                    total: response.total,
                    count: response.items?.length,
                });

                commit('SET_ADMIN_GUEST_LINKS', response.items || []);
                commit('SET_ADMIN_GUEST_LINKS_TOTAL', response.total || 0);
                commit(
                    'SET_ADMIN_GUEST_LINKS_PAGE',
                    response.page || 1
                );
                commit(
                    'SET_ADMIN_GUEST_LINKS_PER_PAGE',
                    response.per_page || 20
                );
            } catch (error) {
                devLog('loadAdminGuestLinks', 'Error', {
                    status: error?.response?.status,
                    message: error?.message,
                });
                commit('SET_ADMIN_GUEST_LINKS_BAD_REQUEST', true);
                commit('SET_ADMIN_GUEST_LINKS', []);
                commit('SET_ADMIN_GUEST_LINKS_TOTAL', 0);
            } finally {
                commit('SET_ADMIN_GUEST_LINKS_LOADING', false);
            }
        },
    },
};
